// DropZoneRegistry (IMPLEMENTATION §2.7 "Drop resolution order" step 2, UX §0): the DOM drop zones by their
// client rects — Sarge's counter plus the tab bar (tabs are inert during a drag) → sell; the Build bar's Sell
// tool → 100% refund / item sale; the Shop sheet's 28 px peek → cancel a shop drag; the Gallery card's gun
// slot → mount (v0.2). Rects come from providers (the UI's DOM elements) or, by default, from the layout;
// they are re-read on relayout, on a mode switch and when a drag starts — never per pointermove.
import { clientToCell, type Layout, type Rect, rectContains } from '../layout/computeLayout.ts';
import type { Mode } from './modes.ts';

export type ZoneId = 'counter' | 'sell' | 'peek' | 'gunSlot';

export const ZONE_IDS: readonly ZoneId[] = ['counter', 'sell', 'peek', 'gunSlot'];

/** The Build bar replaces the counter (same rect): Move · Chute · Sell · Undo in equal slots. */
export const BUILD_BAR = ['move', 'chute', 'sell', 'undo'] as const;
export type BuildBarSlot = (typeof BUILD_BAR)[number];

export function buildBarSlot(layout: Layout, slot: BuildBarSlot): Rect {
  const i = BUILD_BAR.indexOf(slot);
  const w = layout.counter.w / BUILD_BAR.length;
  return { x: layout.counter.x + i * w, y: layout.counter.y, w, h: layout.counter.h };
}

export type DropTarget =
  | { k: 'zone'; zone: ZoneId }
  | { k: 'cell'; x: number; y: number }
  | { k: 'none' };

/** Which zones take part in a drag / tap-target. */
export interface ZoneQuery {
  mode: Mode;
  /** The payload comes from a Shop card (the sheet is collapsed to its peek). */
  shop: boolean;
}

export interface DropZoneRegistry {
  /** A DOM element's rect for a zone (null → back to the layout default; gunSlot has none). */
  provide(id: ZoneId, rect: (() => Rect | null) | null): void;
  /** Re-reads every rect. */
  refresh(layout: Layout, peekPx: number): void;
  rects(): Readonly<Record<ZoneId, Rect | null>>;
  /**
   * The zone under (x, y). Order (§2.7): the peek first for shop drags (it overlaps the counter's lower
   * band: M0 decision 3), then counter ∪ tabs (not in Build: the Build bar replaces the counter), the Sell
   * tool (Build only), the gun slot.
   */
  hit(x: number, y: number, q: ZoneQuery): ZoneId | null;
}

function union(a: Rect, b: Rect): Rect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
}

function defaults(layout: Layout, peekPx: number): Record<ZoneId, Rect | null> {
  return {
    counter: union(layout.counter, layout.tabs),
    sell: buildBarSlot(layout, 'sell'),
    peek: { x: layout.column.x, y: layout.tabs.y - peekPx, w: layout.column.w, h: peekPx },
    gunSlot: null,
  };
}

export function createDropZones(): DropZoneRegistry {
  const providers: Partial<Record<ZoneId, () => Rect | null>> = {};
  let r: Record<ZoneId, Rect | null> = { counter: null, sell: null, peek: null, gunSlot: null };
  return {
    provide(id, rect) {
      if (rect === null) delete providers[id];
      else providers[id] = rect;
    },
    refresh(layout, peekPx) {
      const def = defaults(layout, peekPx);
      const next: Record<ZoneId, Rect | null> = { ...def };
      for (const id of ZONE_IDS) {
        const p = providers[id];
        if (p) next[id] = id === 'counter' ? unionOrNull(p(), layout.tabs) : p();
      }
      r = next;
    },
    rects: () => r,
    hit(x, y, q) {
      const order: ZoneId[] = [];
      if (q.shop) order.push('peek');
      if (q.mode !== 'build') order.push('counter');
      if (q.mode === 'build') order.push('sell');
      order.push('gunSlot');
      for (const id of order) {
        const rect = r[id];
        if (rect && rectContains(rect, x, y)) return id;
      }
      return null;
    },
  };
}

function unionOrNull(a: Rect | null, tabs: Rect): Rect | null {
  return a === null ? null : union(a, tabs);
}

/** DOM zones first (by hotspot), then the board cell, else none (§2.7 order 1–3; no hysteresis). */
export function resolveTarget(
  zones: DropZoneRegistry,
  layout: Layout,
  hx: number,
  hy: number,
  q: ZoneQuery,
): DropTarget {
  const zone = zones.hit(hx, hy, q);
  if (zone) return { k: 'zone', zone };
  const cell = clientToCell(layout, hx, hy);
  if (cell) return { k: 'cell', x: cell.x, y: cell.y };
  return { k: 'none' };
}
